import {
  DriversService,
  SupportMessagesService,
  type PostDriverRequest,
  type PutDriverRequest
} from '@/backend/market'
import { DriverRepository } from '../../domain/repositories/DriverRepository'
import { DriverModel } from '../models/DriverModel'
import type { DriverFilterEntity } from '../../domain/entities/DriverFilterEntity'
import { DriverFilterModel } from '../models/DriverFilterModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { DriverEntity } from '../../domain/entities/DriverEntity'
import type { CreateUpdateDriverEntity } from '../../domain/entities/CreateUpdateDriverEntity'
import { CreateUpdateDriverModel } from '../models/CreateUpdateDriverModel'
import { getMiniRegionCase } from '@/features/region/domain/usecases/getMiniRegionUsecase'
import { getUserByIdCase } from '@/features/user/domain/usecases/getUserByIdUsecase'
import { DriverTrackingModel } from '../models/DriverTrackingModel'
import type { DriverTrackingEntity } from '../../domain/entities/DriverTrackingEntity'
import { DriverTimeLineModel } from '../models/DriverTimeLineModel'
import type { DriverTimeLineEntity } from '../../domain/entities/DriverTimeLineEntity'
import type { DriverStatus } from '../../domain/enums/DriverStatus'
import { DriverPerformanceModel } from '../models/DriverPerformanceModel'
import type { DriverPerformanceEntity } from '../../domain/entities/DriverPerformanceEntity'
import { DriverListAssignmentModel } from '../models/DriverListAssignmentModel'
import { DriverListAssignmentFilterModel } from '../models/DriverListAssignmentFilterModel'
import type { DriverListAssignmentFilterEntity } from '../../domain/entities/DriverListAssignmentFilterEntity'
import moment from 'moment'
import type { DriverAssignmentFilterEntity } from '../../domain/entities/DriverAssignmentFilterEntity'
import { DriverAssignmentFilterModel } from '../models/DriverAssignmentFilterModel'
import { DriverAssignmentModel } from '../models/DriverAssignmentModel'
import type { DriverAssignmentEntity } from '../../domain/entities/DriverAssignmentEntity'
import { DriverMetricsService } from '../services/DriverMetricsService'
import type {
  DriverShiftAdherenceEntity,
  DriverShiftAdherenceDetailEntity
} from '../../domain/entities/DriverShiftAdherenceEntity'
import type {
  DriverAcceptanceRateEntity,
  DriverAcceptanceRateDetailEntity
} from '../../domain/entities/DriverAcceptanceRateEntity'

export class DriverRepositoryImpl implements DriverRepository {
  async getDrivers(
    filterData: DriverFilterEntity
  ): Promise<PaginateEntity<DriverEntity[]> | { error: true }> {
    const driverFilterModel = new DriverFilterModel(filterData)
    try {
      const response = await DriversService.getDriversAdmin(driverFilterModel)
      const driverModels = response.items?.map((item) => new DriverModel(item))
      const result = new PaginateEntity<DriverModel[]>(response, driverModels)
      return result
    } catch {
      return { error: true }
    }
  }
  async getAvailableDrivers(
    filterData: DriverFilterEntity
  ): Promise<DriverEntity[] | { error: true }> {
    const driverFilterModel = new DriverFilterModel(filterData)
    try {
      const response = await DriversService.getAvailableDriversAdmin(driverFilterModel as {})
      const driverModels = response.availableDrivers?.map((item) => new DriverModel(item))
      return driverModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getDriver(id: number): Promise<DriverEntity | { error: true }> {
    try {
      const response = await DriversService.getDriverAdmin({
        id
      })
      const region = response.regionId ? await getMiniRegionCase(response.regionId) : undefined
      const user = response.userId ? await getUserByIdCase(response.userId) : undefined
      const driverModel = new DriverModel(response, region, user)
      return driverModel
    } catch {
      return { error: true }
    }
  }
  async getDriverPerformance(filterData: {
    id: number
    fromDate?: string
    toDate?: string
  }): Promise<DriverPerformanceEntity | { error: true }> {
    try {
      const response = await DriversService.getDriverPerformanceAdmin({
        id: filterData.id,
        fromDate: filterData.fromDate ?? '',
        toDate: filterData.toDate ?? ''
      })
      const driverPerformanceModel = new DriverPerformanceModel(response)
      return driverPerformanceModel
    } catch {
      return { error: true }
    }
  }
  async createDriver(
    driverData: CreateUpdateDriverEntity
  ): Promise<{ success: true } | { error: true }> {
    const driverModel = new CreateUpdateDriverModel(driverData)
    try {
      await DriversService.postDriverAdmin({
        requestBody: driverModel as PostDriverRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateDriver(
    id: number,
    driverData: CreateUpdateDriverEntity
  ): Promise<{ success: true } | { error: true }> {
    const driverModel = new CreateUpdateDriverModel(driverData)
    try {
      await DriversService.putDriverAdmin({
        id,
        requestBody: driverModel as PutDriverRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteDriver(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await DriversService.deleteDriverAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getDriverTrackingDetail(id: number): Promise<DriverTrackingEntity | { error: true }> {
    try {
      const response = await DriversService.getDriverTrackingDetailAdmin({
        id
      })
      const driverTrackingModel = new DriverTrackingModel(response)
      return driverTrackingModel
    } catch {
      return { error: true }
    }
  }
  async getDriverSession(
    id: number,
    filterData: {
      fromDate: string
      toDate: string
      page?: number
      limit?: number
    }
  ): Promise<PaginateEntity<{ startAt: string; endAt: string }[]> | { error: true }> {
    try {
      const response = await DriversService.getDriverSessionsAdmin({
        id,
        fromDate: moment(filterData.fromDate).format(''),
        toDate: moment(filterData.toDate).format(''),
        page: filterData.page,
        limit: filterData.limit
      })
      return response as PaginateEntity<{ startAt: string; endAt: string }[]>
    } catch {
      return { error: true }
    }
  }
  async getDriverTimeline(filterData: {
    id: number
    fromDate: string
    toDate: string
    includeMovements?: boolean
  }): Promise<DriverTimeLineEntity[] | { error: true }> {
    try {
      const response = await DriversService.getDriverTimelineAdmin({
        id: filterData.id,
        fromDate: filterData.fromDate,
        toDate: filterData.toDate,
        includeMovements: filterData.includeMovements
      })
      const driverTrackingModel = response.timelines?.map((item) => new DriverTimeLineModel(item))
      return driverTrackingModel ?? []
    } catch {
      return { error: true }
    }
  }
  async checkUserExist(phoneNumber: string): Promise<
    | {
        usedByDriverId?: number | null
        usedByUserId?: string | null
      }
    | { error: true }
  > {
    try {
      const response = await DriversService.getPhoneNumberCheckAdmin({
        phoneNumber
      })
      return response
    } catch {
      return { error: true }
    }
  }
  async updateDriverStatus(
    id: number,
    status: DriverStatus.OFFLINE | DriverStatus.ONLINE
  ): Promise<{ success: true } | { error: true }> {
    try {
      await DriversService.putDriverStatusAdmin({
        id,
        requestBody: {
          newStatus: status
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async sendMessageNotificationToDriver(
    id: string,
    message: string
  ): Promise<{ success: true } | { error: true }> {
    try {
      await SupportMessagesService.postDriverMessageAdmin({
        requestBody: {
          message,
          driverUserId: id
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getDriverListAssignments(
    filterData: DriverListAssignmentFilterEntity
  ): Promise<PaginateEntity<DriverListAssignmentModel[]> | { error: true }> {
    const driverListAssignmentFilterModel = new DriverListAssignmentFilterModel(filterData)
    try {
      const response = await DriversService.getSearchDriversAdminAsyncAdmin(
        driverListAssignmentFilterModel
      )
      const driverListAssignmentModels = response.items?.map(
        (item) => new DriverListAssignmentModel(item)
      )
      const result = new PaginateEntity<DriverListAssignmentModel[]>(
        response,
        driverListAssignmentModels ?? []
      )
      return result
    } catch {
      return { error: true }
    }
  }
  async getDriverDetailAssignments(
    filterData: DriverAssignmentFilterEntity
  ): Promise<PaginateEntity<DriverAssignmentEntity[]> | { error: true }> {
    const driverAssignmentFilterModel = new DriverAssignmentFilterModel(filterData)
    try {
      const response = await DriversService.getSearchDriverDetailsAdminAsycAdmin(
        driverAssignmentFilterModel
      )
      const driverListAssignmentModels = response.items?.map(
        (item) => new DriverAssignmentModel(item)
      )
      const result = new PaginateEntity<DriverAssignmentModel[]>(
        response,
        driverListAssignmentModels ?? []
      )
      return result
    } catch {
      return { error: true }
    }
  }
  async getDriverShiftAdherence(
    id: number,
    filterData: {
      fromDate: string
      toDate: string
      page?: number
      limit?: number
    }
  ): Promise<PaginateEntity<DriverShiftAdherenceEntity[]> | { error: true }> {
    try {
      const response = await DriverMetricsService.getDriverShiftAdherenceAdmin({
        driverId: id,
        fromDate: moment(filterData.fromDate).format('YYYY-MM-DD'),
        toDate: moment(filterData.toDate).format('YYYY-MM-DD'),
        page: filterData.page,
        limit: filterData.limit
      })
      return response as PaginateEntity<DriverShiftAdherenceEntity[]>
    } catch {
      return { error: true }
    }
  }
  async getDriverShiftAdherenceDetail(
    id: number,
    driverShiftId: string
  ): Promise<DriverShiftAdherenceDetailEntity | { error: true }> {
    try {
      const response = await DriverMetricsService.getDriverShiftAdherenceDetailAdmin({
        driverId: id,
        driverShiftId
      })
      return response as DriverShiftAdherenceDetailEntity
    } catch {
      return { error: true }
    }
  }
  async getDriverAcceptanceRate(
    id: number,
    filterData: {
      fromDate: string
      toDate: string
      page?: number
      limit?: number
    }
  ): Promise<PaginateEntity<DriverAcceptanceRateEntity[]> | { error: true }> {
    try {
      const response = await DriverMetricsService.getDriverAcceptanceRateAdmin({
        driverId: id,
        fromDate: moment(filterData.fromDate).format('YYYY-MM-DD'),
        toDate: moment(filterData.toDate).format('YYYY-MM-DD'),
        page: filterData.page,
        limit: filterData.limit
      })
      return response as PaginateEntity<DriverAcceptanceRateEntity[]>
    } catch {
      return { error: true }
    }
  }
  async getDriverAcceptanceRateDetail(
    id: number,
    date: string
  ): Promise<DriverAcceptanceRateDetailEntity | { error: true }> {
    try {
      const response = await DriverMetricsService.getDriverAcceptanceRateDetailAdmin({
        driverId: id,
        date: moment(date).format('YYYY-MM-DD')
      })
      return response as DriverAcceptanceRateDetailEntity
    } catch {
      return { error: true }
    }
  }
}
